import { ref } from 'vue';
import {
  AuthService,
  UsersService,
  AccountsService,
  client
} from '@shared/openapi/ganaderia-api';
import type { User, Account } from '@shared/openapi/ganaderia-api';
import sharedStores from '@shared/shared-stores';

const TOKEN_KEY = 'ganaderiaAccessToken';

export default () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY));
  const user = ref<User | null>(null);
  const account = ref<Account | null>(null);
  const redirectToFromGuard = ref<string>('/home');

  const setToken = (value: string | null) => {
    token.value = value;
    if (value) {
      localStorage.setItem(TOKEN_KEY, value);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  };

  client.instance.interceptors.request.use((config) => {
    if (token.value) {
      config.headers.Authorization = `Bearer ${token.value}`;
    }
    return config;
  });

  const UNSAFE_METHODS = ['post', 'patch', 'delete'];
  client.instance.interceptors.request.use((config) => {
    if (user.value?.username === 'Live-Demo' && UNSAFE_METHODS.includes(config.method || '')) {
      sharedStores.snackbar.funcs.notify({
        text: 'Sorry, you can not change data in the live demo',
        type: 'info',
        timeout: 6000
      });
      return Promise.reject(new Error('live-demo-write-blocked'));
    }
    if (account.value && new Date(account.value.valid_until) < new Date()) {
      sharedStores.snackbar.funcs.notify({
        text: 'Your account is not valid anymore. A new payment is required',
        type: 'warning',
        timeout: 6000
      });
      return Promise.reject(new Error('account-overdue'));
    }
    return config;
  });

  const login = async (username: string, password: string) => {
    const res = await AuthService.loginForAccessTokenV1AuthLoginPost({
      body: { username, password }
    });
    if (res.error || !res.data) throw res;
    setToken(res.data.access_token);
  };

  const logout = () => {
    setToken(null);
    user.value = null;
    account.value = null;
  };

  const fetchAccount = async () => {
    if (!user.value) return;
    const res = await AccountsService.getAccountV1AccountsIdGet({
      path: { id: user.value.account_id }
    });
    account.value = res.data ?? null;
  };

  const fetchUser = async () => {
    const res = await UsersService.readUsersMeV1UsersMeGet();
    if (res.error || !res.data) throw res;
    user.value = res.data;
    await fetchAccount();
    return user.value;
  };

  const refreshToken = async () => {
    const res = await AuthService.refreshTokenV1AuthRefreshTokenGet();
    if (res.data) setToken(res.data.access_token);
  };

  const ensureUser = async () => {
    const fetchedUser = await fetchUser();
    await refreshToken();
    return fetchedUser;
  };

  const registrate = async (
    email: string,
    username: string,
    password: string,
    promoCode?: string
  ) => {
    const res = await AuthService.sendRegistrationEmailV1AuthRegistratePost({
      body: { email, username, password, promo_code: promoCode || undefined }
    });
    if (res.error) throw res;
  };

  const verifyRegistrationToken = async (registrationToken: string) => {
    const res = await AuthService.verifyRegistrationTokenV1AuthVerifyRegistrationTokenGet({
      path: { token: registrationToken }
    });
    if (res.error) throw res;
  };

  const checkExists = async (type: 'username' | 'email', value: string) => {
    const res =
      type === 'username'
        ? await AuthService.checkUsernameExistsV1AuthExistsUsernamePost({
            body: { username: value }
          })
        : await AuthService.checkEmailExistsV1AuthExistsEmailPost({ body: { email: value } });
    return !res.error;
  };

  const sendPasswordResetEmail = async (email: string) => {
    const res = await AuthService.getResetPasswordTokenV1AuthSendResetPasswordEmailPost({
      body: { email }
    });
    if (res.error) throw res;
  };

  const sendResetPassword = async (password: string, resetToken: string) => {
    const res = await AuthService.resetPasswordV1AuthResetPasswordPost({
      body: { password, token: resetToken }
    });
    if (res.error) throw res;
  };

  const sendUpdateUsername = async (username: string) => {
    const res = await AuthService.updateUsernameV1AuthUpdateUsernamePost({ body: { username } });
    if (res.error) throw res;
    if (user.value) user.value.username = username;
  };

  const sendUpdatePassword = async (password: string) => {
    const res = await AuthService.updatePasswordV1AuthUpdatePasswordPost({ body: { password } });
    if (res.error) throw res;
  };

  const savePayment = async (payment: string, months: number) => {
    const res = await AuthService.savePaymentRouteV1AuthSavePaymentPost({
      body: { payment, months }
    });
    if (res.error) throw res;
    await fetchAccount();
  };

  const loginLiveDemo = async () => {
    await login('Live Demo', 'live-demo');
    const loggedInUser = await fetchUser();
    return loggedInUser;
  };

  return {
    state: { token, user, account, redirectToFromGuard },
    funcs: {
      login,
      logout,
      fetchAccount,
      fetchUser,
      refreshToken,
      ensureUser,
      registrate,
      verifyRegistrationToken,
      checkExists,
      sendPasswordResetEmail,
      sendResetPassword,
      sendUpdateUsername,
      sendUpdatePassword,
      savePayment,
      loginLiveDemo
    }
  };
};
